import { Editor, Node as SlateNode, type Selection } from 'slate';
import { ReactEditor } from 'slate-react';

// Convert a selection into an array of rectangles in the DOM.
export default function positionOfCursorInDocument(
  editor: Editor,
  selection: Selection
) {
  if (!selection) return [];

  const anchorPath = selection.anchor.path;
  const focusPath = selection.focus.path;
  if (
    !Editor.hasPath(editor, anchorPath) ||
    !Editor.hasPath(editor, focusPath)
  ) {
    return [];
  }

  const anchorNode = SlateNode.descendant(editor, anchorPath);
  if (!anchorNode) {
    console.error(
      `Couldn't draw a selection at path: ${JSON.stringify(
        selection.anchor.path
      )}`
    );
    return [];
  }

  const focusNode = SlateNode.descendant(editor, focusPath);
  if (!focusNode) {
    console.error(
      `Couldn't draw a selection at path: ${JSON.stringify(
        selection.focus.path
      )}`
    );
    return [];
  }

  const domRange = ReactEditor.toDOMRange(editor, selection);
  if (!domRange) return [];

  return textNodeRects(domRange);
}

// Get client rects for text nodes within a DOM range.

// There was some code to merge overlapping text nodes here, but it was
// only being used to merge some very small rects that through testing
// are invisible to the user anyway.

// If we come across a case where we see overlapping selection overlays,
// we can revisit this. For now, we will just return the rects as they
// are and avoid the expense of merging them.
function textNodeRects(domRange: Range) {
  // If start and end containers are the same text node, handle directly
  if (
    domRange.startContainer === domRange.endContainer &&
    domRange.startContainer.nodeType === Node.TEXT_NODE
  ) {
    return Array.from(domRange.getClientRects());
  }

  // Create a DOM TreeWalker to iterate through the nodes within the
  // common ancestor of the range. We only walk text nodes here and we
  // use a filter to make sure they have content and intersect the range.
  const walker = document.createTreeWalker(
    domRange.commonAncestorContainer,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node) => {
        const hasLength = node.textContent && node.textContent.trim().length;
        const intersectsRange = domRange.intersectsNode(node);

        return hasLength && intersectsRange
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      },
    }
  );

  const textRects: DOMRect[] = [];

  // For each text node, get appropriate DOMRects by creating a
  // temporary range that represents the portion of the text node
  // that is within the selection. We then get the client rects for
  // that range and add them to the array of textRects.
  while (walker.nextNode()) {
    const textNode = walker.currentNode as Text;
    const tempRange = document.createRange();

    // Different handling based on whether this is a start, middle, or end node
    if (textNode === domRange.startContainer) {
      // This is the start node - get from offset to end
      tempRange.setStart(textNode, domRange.startOffset);
      tempRange.setEnd(textNode, textNode.length);
    } else if (textNode === domRange.endContainer) {
      // This is the end node - get from start to offset
      tempRange.setStart(textNode, 0);
      tempRange.setEnd(textNode, domRange.endOffset);
    } else if (
      domRange.comparePoint(textNode, 0) <= 0 &&
      domRange.comparePoint(textNode, textNode.length) >= 0
    ) {
      // This is a middle node (node is fully within the range) - get entire node
      tempRange.selectNodeContents(textNode);
    } else {
      // Node is not in range
      continue;
    }

    // Add rects for this portion of text
    for (const rect of tempRange.getClientRects()) {
      textRects.push(rect);
    }
  }

  return textRects;
}
